import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { X, Plus } from "lucide-react";
import { toast } from "sonner";

const PALETTE = ["#2563EB", "#0EA5E9", "#10B981", "#F59E0B", "#EF4444", "#A855F7", "#EC4899", "#14B8A6", "#F97316", "#6366F1", "#71717A"];

function defaultColor(seed) {
  if (!seed) return PALETTE[0];
  let h = 0;
  for (const ch of seed) h = (h * 31 + ch.charCodeAt(0)) & 0xFFFFFFFF;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

/**
 * Team-wide shared tags on a thread. Visible to all employees.
 * Chips match pipeline-status visuals: small colored dot + label.
 */
export default function TeamTagsEditor({ threadId, initial, allTags, onChange }) {
  const [tags, setTags] = useState(initial || []);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [openColor, setOpenColor] = useState(null);
  const inputRef = useRef();

  useEffect(() => { setTags(initial || []); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [threadId]);

  const colorOf = (t) => {
    const found = (allTags || []).find((x) => x.tag === t);
    return found?.color || defaultColor(t);
  };

  const persist = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/threads/${threadId}/team-tags`, { tags: next });
      setTags(data.tags);
      onChange?.(data.tags);
    } catch (e) {
      toast.error("Couldn't save team tag");
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    const t = input.trim();
    if (!t) return;
    if (tags.includes(t)) { setInput(""); return; }
    const next = [...tags, t];
    setInput("");
    persist(next);
  };

  const removeTag = (t) => persist(tags.filter((x) => x !== t));

  const setColor = async (t, color) => {
    try {
      await api.put(`/team-tags/${encodeURIComponent(t)}/color`, { color });
      setOpenColor(null);
      onChange?.(tags);
    } catch (e) {
      toast.error("Couldn't update color");
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    } else if (e.key === "Backspace" && !input && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="team-tags-editor">
      <span className="text-[10px] font-semibold tracking-[0.05em] uppercase text-zinc-500 mr-1">
        Team tags
      </span>
      {tags.map((t) => {
        const c = colorOf(t);
        const picking = openColor === t;
        return (
          <span
            key={t}
            className="relative inline-flex items-center gap-1.5 pl-2 pr-1 py-0.5 text-xs bg-white text-zinc-800 rounded-full border border-zinc-200 hover:border-zinc-300 transition-colors"
            data-testid={`team-tag-chip-${t}`}
          >
            <button
              type="button"
              onClick={() => setOpenColor(picking ? null : t)}
              className="w-2 h-2 rounded-full shrink-0"
              style={{ background: c }}
              aria-label={`Change color for ${t}`}
              data-testid={`team-tag-dot-${t}`}
            />
            <span>{t}</span>
            <button
              type="button"
              onClick={() => removeTag(t)}
              className="ml-0.5 hover:bg-zinc-100 rounded-full p-0.5 transition-colors"
              aria-label={`Remove ${t}`}
              data-testid={`team-tag-remove-${t}`}
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
            {picking && (
              <div className="absolute z-20 top-full left-0 mt-1 bg-white border border-zinc-200 rounded-md p-1.5 shadow-md flex gap-1" data-testid={`team-tag-color-picker-${t}`}>
                {PALETTE.map((p) => (
                  <button
                    key={p}
                    onClick={() => setColor(t, p)}
                    className={`w-4 h-4 rounded-full hover:scale-110 transition-transform ${c.toLowerCase() === p.toLowerCase() ? "ring-2 ring-offset-1 ring-zinc-900" : ""}`}
                    style={{ background: p }}
                    aria-label={`Color ${p}`}
                  />
                ))}
              </div>
            )}
          </span>
        );
      })}
      <div className="inline-flex items-center gap-0.5">
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          placeholder={tags.length === 0 ? "Add a team tag…" : "+ tag"}
          className="h-6 text-xs px-1.5 border-0 bg-transparent focus:outline-none focus:ring-0 placeholder:text-zinc-400 w-24"
          disabled={saving}
          data-testid="team-tag-input"
        />
        {input.trim() && (
          <button
            type="button"
            onClick={addTag}
            className="p-0.5 text-emerald-600 hover:bg-emerald-100 rounded transition-colors"
            data-testid="team-tag-add"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        )}
      </div>
    </div>
  );
}
